import { useCallback, useMemo } from 'react';
import { getSelectRowId } from 'utils/table';

interface UseBulkSelectAllParams<TRow extends { id: string; identifier: string }> {
  selectedRows: string[];
  currentPageRows: TRow[];
  isHeliosTable: boolean;
  onSelectionChange: (ids: string[]) => void;
}

const useBulkSelectAll = <TRow extends { id: string; identifier: string }>({
  selectedRows,
  currentPageRows,
  isHeliosTable,
  onSelectionChange,
}: UseBulkSelectAllParams<TRow>) => {
  const currentPageRowIds = useMemo(() => currentPageRows.map((row) => getSelectRowId(row, isHeliosTable)), [currentPageRows, isHeliosTable]);
  const selectedRowIdsSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const currentPageRowIdsSet = useMemo(() => new Set(currentPageRowIds), [currentPageRowIds]);

  const isAllCurrentPageSelected = useMemo(
    () => currentPageRowIds.length > 0 && currentPageRowIds.every((id) => selectedRowIdsSet.has(id)),
    [currentPageRowIds, selectedRowIdsSet]
  );

  const toggleSelectAll = useCallback(() => {
    if (isAllCurrentPageSelected) {
      onSelectionChange(selectedRows.filter((id) => !currentPageRowIdsSet.has(id)));
    } else {
      onSelectionChange([...new Set([...selectedRows, ...currentPageRowIds])]);
    }
  }, [currentPageRowIds, currentPageRowIdsSet, isAllCurrentPageSelected, onSelectionChange, selectedRows]);

  return { isAllCurrentPageSelected, toggleSelectAll };
};

export { useBulkSelectAll };
